import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { payoutService } from "@/services";
import type { PaymentMode, PayoutView } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { canApprovePayouts, canManageFinancials, canReversePayouts } from "@/config/roles";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { Alert } from "@/components/base/Alert";
import { ErrorState } from "@/components/base/ErrorState";
import { TextField } from "@/components/base/TextField";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatINRNumber } from "@/utils/format";
import { payoutStatusMeta } from "../components/labels";
import { WinnerPanel } from "./components/WinnerPanel";
import { CycleContextPanel } from "./components/CycleContextPanel";
import { FundingPanel } from "./components/FundingPanel";
import { CalculationStatement } from "./components/CalculationStatement";
import { ApproveModal } from "./components/ApproveModal";
import { DisbursementModal } from "./components/DisbursementModal";
import { ReversalModal } from "./components/ReversalModal";

export default function PayoutProcessPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const toast = useToast();
  const actor = user?.name ?? "Staff User";

  const [payout, setPayout] = useState<PayoutView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [disburseOpen, setDisburseOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [showVoucher, setShowVoucher] = useState(false);

  // manual deduction form
  const [deductionOpen, setDeductionOpen] = useState(false);
  const [dedAmount, setDedAmount] = useState<number | null>(null);
  const [dedReason, setDedReason] = useState("");
  const [dedError, setDedError] = useState<string | null>(null);
  const [dedSubmitting, setDedSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      setPayout(await payoutService.getPayout(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this payout.");
      setPayout(null);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (remarks: string) => {
    if (!id) return false;
    try {
      await payoutService.approvePayout(id, remarks, actor);
      toast.success("Payout approved", `${payout?.winnerName ?? "Winner"} authorised for disbursement.`);
      await load();
      return true;
    } catch (err) {
      toast.error("Could not approve", err instanceof Error ? err.message : "Please try again.");
      return false;
    }
  };

  const disburse = async (input: { amount: number; mode: PaymentMode; reference: string; paidOn: string; remarks: string }) => {
    if (!id) return false;
    try {
      await payoutService.recordDisbursement(id, {
        amount: input.amount,
        mode: input.mode,
        reference: input.reference,
        paidOn: input.paidOn,
        remarks: input.remarks,
        actor,
      });
      toast.success("Disbursement recorded", `Voucher ${payout?.voucherNumber ?? ""} issued.`);
      setShowVoucher(true);
      await load();
      return true;
    } catch (err) {
      toast.error("Could not record disbursement", err instanceof Error ? err.message : "Please try again.");
      return false;
    }
  };

  const addDeduction = async () => {
    if (!id) return;
    if (!dedReason.trim()) {
      setDedError("A reason is required for the deduction.");
      return;
    }
    setDedSubmitting(true);
    setDedError(null);
    try {
      await payoutService.addManualDeduction(id, dedAmount ?? 0, dedReason, actor);
      toast.success("Deduction added", "The payout calculation has been updated.");
      setDeductionOpen(false);
      setDedAmount(null);
      setDedReason("");
      await load();
    } catch (err) {
      setDedError(err instanceof Error ? err.message : "Could not add the deduction.");
    } finally {
      setDedSubmitting(false);
    }
  };

  const [reopening, setReopening] = useState(false);

  const handleReopen = async () => {
    if (!id || reopening) return;
    setReopening(true);
    try {
      await payoutService.reopenPayout(id);
      toast.success("Payout re-opened", `${payout?.voucherNumber ?? ""} is now awaiting review and approval.`);
      await load();
    } catch (err) {
      toast.error("Could not re-open", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setReopening(false);
    }
  };

  const reverse = async (reason: string) => {
    if (!id) return false;
    try {
      await payoutService.reversePayout(id, reason, actor);
      toast.warning("Payout reversed", `${payout?.voucherNumber ?? ""} disbursement cancelled and marked as reversed.`);
      setShowVoucher(false);
      await load();
      return true;
    } catch (err) {
      toast.error("Could not reverse", err instanceof Error ? err.message : "Please try again.");
      return false;
    }
  };

  const back = (
    <Button variant="outline" size="sm" icon={<i className="ri-arrow-left-line" />} onClick={() => navigate("/payout")}>
      Back to payouts
    </Button>
  );

  const breadcrumb = payout ? (
    <>
      <BreadcrumbItem label="Home" to="/" />
      <BreadcrumbItem label="Payout" to="/payout" />
      <BreadcrumbItem label={`${payout.groupName} · Cycle ${payout.cycle}`} isLast />
    </>
  ) : (
    <>
      <BreadcrumbItem label="Home" to="/" />
      <BreadcrumbItem label="Payout" to="/payout" />
      <BreadcrumbItem label="Payout" isLast />
    </>
  );

  if (error) {
    return (
      <div>
        <PageHeader breadcrumb={breadcrumb} title="Payout" actions={back} />
        <Card><ErrorState message={error} onRetry={load} /></Card>
      </div>
    );
  }

  if (!payout) {
    return (
      <div>
        <PageHeader breadcrumb={breadcrumb} title="Loading payout…" actions={back} />
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <div className="h-56 animate-pulse rounded-lg bg-background-200 lg:col-span-2" />
            <div className="h-56 animate-pulse rounded-lg bg-background-200" />
          </div>
        </div>
      </div>
    );
  }

  const meta = payoutStatusMeta(payout.status);
  const notReversed = payout.status !== "reversed";
  const pendingApproval = payout.status === "pending_approval";
  const readyToPay = payout.status === "approved" || payout.status === "partially_disbursed";
  const canPrepare = canManageFinancials(role);
  const collectionIncomplete = payout.expectedCollection > 0 && payout.collected < payout.expectedCollection;

  return (
    <div>
      <PageHeader
        breadcrumb={breadcrumb}
        title={`${payout.groupName} · Cycle ${payout.cycle}`}
        subtitle={`${payout.periodLabel} · Voucher ${payout.voucherNumber}`}
        actions={
          <>
            <Button variant="outline" size="sm" icon={<i className="ri-printer-line" />} onClick={() => navigate(`/payout/${payout.id}/voucher`)}>
              View voucher
            </Button>
            {back}
          </>
        }
      />

      {payout.status === "reversed" ? (
        <div className="mb-3">
          <Alert
            variant="error"
            title="Reversed payout"
            action={
              canApprovePayouts(role) ? (
                <Button size="sm" variant="primary" icon={<i className="ri-restart-line" />} loading={reopening} onClick={handleReopen}>
                  Re-open for review & approval
                </Button>
              ) : null
            }
          >
            Reversed on {formatDate(payout.reversedAt!)} {payout.reversedBy ? `by ${payout.reversedBy}` : ""}. Reason: <strong>{payout.reversalReason}</strong>.
            The customer disbursement transaction was cancelled. Click "Re-open" to move this payout back to Awaiting Approval for review and re-disbursement.
          </Alert>
        </div>
      ) : null}

      {showVoucher ? (
        <div className="mb-3">
          <Alert
            variant="success"
            title={`Disbursement recorded — voucher ${payout.voucherNumber}`}
            action={
              <Button size="sm" variant="outline" icon={<i className="ri-printer-line" />} onClick={() => navigate(`/payout/${payout.id}/voucher`)}>
                Print voucher
              </Button>
            }
          >
            Payment of {payout.amountPaid.toLocaleString("en-IN")} recorded. {payout.balanceOwed > 0
              ? `Balance of ${payout.balanceOwed.toLocaleString("en-IN")} still owed.`
              : "This payout is fully paid."}
          </Alert>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <WinnerPanel payout={payout} />
          <CycleContextPanel payout={payout} />
          <FundingPanel payout={payout} />
        </div>

        <div className="space-y-4">
          <CalculationStatement payout={payout} />

          {notReversed && (pendingApproval || readyToPay) ? (
            <Card title="Manual deductions" subtitle="Adjustments agreed verbally, each with a reason">
              {payout.manualDeductions.length > 0 ? (
                <div className="mb-3 space-y-1.5">
                  {payout.manualDeductions.map((d) => (
                    <div key={d.id} className="flex items-center justify-between rounded-md bg-background-100/60 px-3 py-1.5 text-sm">
                      <span className="text-foreground-700">{d.reason}</span>
                      <span className="tnum font-medium text-danger-600"><CurrencyDisplay amount={d.amount} hideSymbol /></span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mb-3 text-xs text-foreground-500">No manual deductions yet.</p>
              )}
              {deductionOpen ? (
                <div className="space-y-2 rounded-md border border-background-200 p-3">
                  <CurrencyInput label="Deduction amount" value={dedAmount} onChange={setDedAmount} required />
                  <TextField
                    label="Reason (required)"
                    placeholder="Why this amount is being withheld…"
                    value={dedReason}
                    onChange={(e) => setDedReason(e.target.value)}
                  />
                  {dedError ? <p className="text-xs text-danger-600">{dedError}</p> : null}
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setDeductionOpen(false)} disabled={dedSubmitting}>
                      Cancel
                    </Button>
                    <Button size="sm" icon={<i className="ri-add-line" />} loading={dedSubmitting} onClick={addDeduction}>
                      Add deduction
                    </Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" icon={<i className="ri-subtract-line" />} onClick={() => setDeductionOpen(true)}>
                  Add a deduction
                </Button>
              )}
            </Card>
          ) : null}

          <Card title="Actions">
            {pendingApproval ? (
              <div className="space-y-3">
                {canApprovePayouts(role) ? (
                  <div className="space-y-2">
                    <Button fullWidth icon={<i className="ri-check-double-line" />} onClick={() => setApproveOpen(true)}>
                      Approve payout
                    </Button>
                    {collectionIncomplete ? (
                      <Alert variant="info" title="Partial collection so far">
                        Cycle collection is currently at {formatINRNumber(payout.collected)} of {formatINRNumber(payout.expectedCollection)}. Uncollected installment dues remain on member accounts and carry forward to future cycles.
                      </Alert>
                    ) : null}
                  </div>
                ) : (
                  <Alert variant="info" title="Approval restricted">
                    Only Admin and Super Admin can approve a payout. You can prepare the payout but not authorise it.
                  </Alert>
                )}
                <p className="text-xs text-foreground-500">
                  Approval is a distinct step from handing over the money. Once approved, the payout can be disbursed.
                </p>
              </div>
            ) : readyToPay ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-md bg-background-100/60 px-3 py-2 text-sm">
                  <span className="text-foreground-500">Approved by</span>
                  <span className="font-medium text-foreground-900">{payout.approval?.approvedBy ?? "—"}</span>
                </div>
                {payout.approval?.remarks ? (
                  <p className="rounded-md bg-background-100/60 px-3 py-2 text-xs text-foreground-600">
                    {payout.approval.remarks}
                  </p>
                ) : null}
                {canPrepare ? (
                  <Button fullWidth icon={<i className="ri-hand-coin-line" />} onClick={() => setDisburseOpen(true)}>
                    Record disbursement
                  </Button>
                ) : (
                  <Alert variant="info" title="Disbursement restricted">
                    Only Admins and Accountants can record a disbursement.
                  </Alert>
                )}
                {payout.balanceOwed > 0 ? (
                  <p className="text-xs text-foreground-500">
                    Balance still owed: <CurrencyDisplay amount={payout.balanceOwed} hideSymbol />
                  </p>
                ) : (
                  <p className="text-xs text-success-700">Fully paid — this cycle is complete.</p>
                )}
              </div>
            ) : payout.status === "disbursed" ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-md bg-success-100/60 px-3 py-2 text-sm">
                  <span className="text-success-800">Disbursed in full</span>
                  <span className="tnum font-semibold text-success-900"><CurrencyDisplay amount={payout.amountPaid} hideSymbol /></span>
                </div>
                {canReversePayouts(role) ? (
                  <Button
                    fullWidth
                    variant="danger"
                    icon={<i className="ri-close-circle-line" />}
                    onClick={() => setReverseOpen(true)}
                  >
                    Reverse disbursement
                  </Button>
                ) : (
                  <Alert variant="info" title="Reversal restricted">
                    Only Admin and Super Admin can reverse a payout.
                  </Alert>
                )}
              </div>
            ) : payout.status === "reversed" ? (
              <div className="space-y-3">
                <div className="rounded-md bg-danger-50 p-3 text-xs text-danger-700 border border-danger-200">
                  <p className="font-semibold text-danger-900">This payout was reversed</p>
                  <p className="mt-1 text-danger-800">Reason: {payout.reversalReason ?? "Disbursement error"}</p>
                </div>
                {canApprovePayouts(role) ? (
                  <Button
                    fullWidth
                    variant="primary"
                    icon={<i className="ri-restart-line" />}
                    loading={reopening}
                    onClick={handleReopen}
                  >
                    Review & re-approve
                  </Button>
                ) : (
                  <Alert variant="info" title="Approval restricted">
                    Only Admin and Super Admin can re-open a reversed payout.
                  </Alert>
                )}
                <p className="text-xs text-foreground-500">
                  Re-opening will return this voucher to Awaiting Approval so you can review details and disburse payment to the customer again.
                </p>
              </div>
            ) : null}
          </Card>
        </div>
      </div>

      {approveOpen ? (
        <ApproveModal payout={payout} onClose={() => setApproveOpen(false)} onConfirm={approve} />
      ) : null}
      {disburseOpen ? (
        <DisbursementModal payout={payout} onClose={() => setDisburseOpen(false)} onConfirm={disburse} />
      ) : null}
      {reverseOpen ? (
        <ReversalModal payout={payout} onClose={() => setReverseOpen(false)} onConfirm={reverse} />
      ) : null}
    </div>
  );
}